import { useEffect, useState, useContext, useRef } from 'react';
import Peer from 'peerjs';
import { AppContext } from '../context/AppContext';
import { getUser } from '../services/adminServices';
import socket from '../services/socketConnection';

function AdminScreenSharing() {
  const { login, appData } = useContext(AppContext);
  const [peerId, setPeerId] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [users, setUsers] = useState([]);
  const [filter] = useState({ name: "", page: 1, limit: 10 });
  const [selectedUser, setSelectedUser] = useState("");
  const [isRemoteControlEnabled, setIsRemoteControlEnabled] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const videoRef = useRef(null);
  const containerRef = useRef(null);

  const getAllUsers = async () => {
    const result = await getUser(login.token, filter);
    setUsers(result.data.map(x => ({ ...x, isGroup: false })));
  };

  const sendPeerIdToUser = (userId) => {
    if (peerId && userId) {
      socket.emit("admin:sendPeerId", { userId, peerId });
    }
  };

  useEffect(() => {
    getAllUsers();
    const url = localStorage.getItem("ipConfig");
    if (!url || Object.keys(appData).length === 0) return;
    if (peerId) return;

    const hostname = new URL(url).hostname;
    const peer = appData.isRemote
      ? new Peer(undefined)
      : new Peer(undefined, {
        host: hostname,
        port: 443,
        secure: true,
        path: "/screen",
        config: {
          iceServers: [
            { urls: "stun:stun.l.google.com:19302" },
            {
              urls: "turn:turn.anyfirewall.com:443?transport=tcp",
              username: "webrtc",
              credential: "webrtc",
            }
          ],
          iceTransportPolicy: "all"
        }
      });


    peer.on('open', (id) => setPeerId(id));

    peer.on('call', (call) => {
      call.answer(null);
      call.on('stream', (remoteStream) => setRemoteStream(remoteStream));
      call.on('error', (err) => console.error('Call error:', err));
    });

    peer.on('error', (err) => console.error('Peer error:', err));

    return () => peer.destroy();
  }, [JSON.stringify(appData)]);

  const handleSelectionChange = (e) => {
    const userId = e.target.value;
    setRemoteStream(null);
    setSelectedUser(userId);
    sendPeerIdToUser(userId);
    setIsModalOpen(true);
  };

  const sendMouse = (e) => {
    if (!isRemoteControlEnabled || !videoRef.current) return;
    const rect = videoRef.current.getBoundingClientRect();
    const normalizedX = (e.clientX - rect.left) / rect.width;
    const normalizedY = (e.clientY - rect.top) / rect.height;

    socket.emit('mouse-move', {
      userId: selectedUser,
      x: normalizedX,
      y: normalizedY,
    });
  };

  const sendRightClick = (e) => {
    e.preventDefault();
    if (!isRemoteControlEnabled) return;
    socket.emit('mouse-click', { userId: selectedUser, button: 'right' });
  };

  const sendScroll = (e) => {
    if (!isRemoteControlEnabled) return;

    socket.emit('mouse-scroll', {
      userId: selectedUser,
      deltaX: e.deltaX,
      deltaY: e.deltaY,
    });

    e.preventDefault();
  };

  const sendDoubleClick = () => {
    if (!isRemoteControlEnabled) return;
    socket.emit('mouse-double-click', { userId: selectedUser });
  };

  const isDraggingRef = useRef(false);

  const handleMouseDown = (e) => {
    if (!isRemoteControlEnabled) return;
    isDraggingRef.current = true;
    socket.emit('mouse-down', {
      userId: selectedUser,
      button: e.button === 2 ? 'right' : e.button === 1 ? 'middle' : 'left'
    });
  };

  const handleMouseUp = (e) => {
    if (!isRemoteControlEnabled) return;
    isDraggingRef.current = false;
    socket.emit('mouse-up', {
      userId: selectedUser,
      button: e.button === 2 ? 'right' : e.button === 1 ? 'middle' : 'left'
    });
  };

  const sendMouseDrag = (e) => {
    if (!isRemoteControlEnabled || !isDraggingRef.current) return;
    sendMouse(e);
  };

  /** --- Shortcut and Key Handling --- **/
  const pressedKeys = useRef(new Set());

  const sendKeyOrShortcut = (e) => {
    if (!isRemoteControlEnabled) return;

    e.preventDefault(); // Stop browser from intercepting Ctrl+C, etc.

    const keyMap = {
      ArrowUp: "up", ArrowDown: "down", ArrowLeft: "left", ArrowRight: "right",
      Control: "control", Alt: "alt", Shift: "shift", Enter: "enter",
      Escape: "escape", Backspace: "backspace", Tab: "tab",
      Meta: "command", CapsLock: "capslock", AltGraph: "alt"
    };

    const keyName = keyMap[e.key] || e.key.toLowerCase();
    pressedKeys.current.add(keyName);

    // If multiple keys are pressed — treat as a shortcut
    if (pressedKeys.current.size > 1) {
      socket.emit("shortcut-keys", {
        userId: selectedUser,
        keys: Array.from(pressedKeys.current)
      });
      return;
    }

    // Otherwise send single key
    socket.emit("key-press", { userId: selectedUser, key: keyName });
  };


  const clearKey = (e) => {
    const keyMap = {
      Control: "control", Alt: "alt", Shift: "shift",
      Meta: "command", AltGraph: "alt"
    };
    const keyName = keyMap[e.key] || e.key.toLowerCase();
    pressedKeys.current.delete(keyName);
  };

  useEffect(() => {
    window.addEventListener("keydown", sendKeyOrShortcut);
    window.addEventListener("keyup", clearKey);
    return () => {
      window.removeEventListener("keydown", sendKeyOrShortcut);
      window.removeEventListener("keyup", clearKey);
    };
  }, [selectedUser, isRemoteControlEnabled]);

  useEffect(() => {
    if (isModalOpen && videoRef.current && remoteStream) {
      videoRef.current.srcObject = remoteStream;
      videoRef.current.play().catch(console.error);
    }
  }, [isModalOpen, remoteStream]);

  useEffect(() => {
    const disableContextMenu = (e) => e.preventDefault();
    window.addEventListener("contextmenu", disableContextMenu);
    return () => window.removeEventListener("contextmenu", disableContextMenu);
  }, []);

  return (
    <div className="container mt-4">
      <h5 className="mb-3">Your Peer ID: <span className="text-primary">{peerId || 'Loading...'}</span></h5>

      <div className="mb-3">
        <label htmlFor="user-select" className="form-label">Select User:</label>
        <select
          id="user-select"
          className="form-select"
          value={selectedUser}
          onChange={handleSelectionChange}
        >
          <option value="">-- Select User --</option>
          {users.map((user) => (
            <option key={user._id} value={user._id}>{user.name}</option>
          ))}
        </select>
      </div>

      <div className="form-check mb-3">
        <input
          className="form-check-input"
          type="checkbox"
          checked={isRemoteControlEnabled}
          onChange={() => setIsRemoteControlEnabled(prev => !prev)}
          id="remote-control-check"
        />
        <label className="form-check-label" htmlFor="remote-control-check">
          Enable Remote Control
        </label>
      </div>

      {remoteStream && !isModalOpen && (
        <button className="btn btn-primary mb-3" onClick={() => setIsModalOpen(true)}>
          Open Screen
        </button>
      )}

      {/* Fullscreen modal overlay */}
      <div
        ref={containerRef}
        style={{ display: isModalOpen ? 'flex' : 'none' }}
        className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-75 flex-column justify-content-center align-items-center"
      >
        <div className="position-absolute top-0 end-0 m-3 d-flex gap-2">
          <button className="btn btn-light" onClick={() => setIsModalOpen(false)}>Close</button>
        </div>

        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          controls={false}
          tabIndex={-1}
          onWheel={sendScroll}
          onContextMenu={sendRightClick}
          onPause={(e) => {
            e.preventDefault();
            videoRef.current?.play().catch(() => { });
          }}
          onMouseMove={(e) => {
            sendMouse(e);
            sendMouseDrag(e);
          }}
          onDoubleClick={sendDoubleClick}
          onMouseUp={handleMouseUp}
          onMouseDown={handleMouseDown}
          className="border border-light"
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            cursor: isRemoteControlEnabled ? "crosshair" : "default",
            outline: 'none',
            pointerEvents: 'auto',
          }}
        />
      </div>

      {!remoteStream && <p className="text-muted">Waiting for screen sharing...</p>}
    </div>
  );
}

export default AdminScreenSharing;
